import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Mail, User, CheckCircle } from 'lucide-react';
import './AdminSetup.css';

function AdminSetup() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    } else {
      setError('No setup token provided. Please use the link from your SMS.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Setup token is missing');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch('https://api.eventpiclive.com/api/admin/setup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // Send cookies
        body: JSON.stringify({ token, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Setup failed');
      }

      // Hybrid auth: Cookie for desktop, sessionStorage for mobile
      console.log('✅ Setup successful!');
      console.log('📝 Token received:', data.token ? 'YES' : 'NO');
      
      if (data.token) {
        sessionStorage.setItem('adminToken', data.token);
        localStorage.setItem('token', data.token); // Also store for main dashboard
        console.log('💾 Token stored');
      } else {
        console.warn('⚠️ No token in response, relying on cookie');
      }
      
      // Store admin data and first name for dashboard
      localStorage.setItem('adminData', JSON.stringify(data.admin));
      const firstName = data.admin.full_name.split(' ')[0];
      localStorage.setItem('venueName', firstName);
      console.log('💾 Admin data stored');
      console.log('🔄 Navigating to main dashboard...');

      // Navigate to main dashboard (not /admin/dashboard)
      navigate('/', { replace: true });

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="admin-setup-container">
        <div className="admin-setup-card success-card">
          <CheckCircle size={64} className="success-icon" />
          <h1>Account Created!</h1>
          <p>Your EventPic Live admin account is ready.</p>
          <p className="redirect-text">Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-setup-container">
      <div className="admin-setup-card">
        <div className="setup-header">
          <h1>Complete Your Setup</h1>
          <p>Create a password for your EventPic Live admin account</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="setup-form">
          <div className="form-group">
            <label htmlFor="password">
              <Lock size={18} />
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              disabled={!token || loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">
              <Lock size={18} />
              Confirm Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter password"
              required
              disabled={!token || loading}
            />
          </div>

          <button
            type="submit"
            className="setup-button"
            disabled={!token || loading}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>

        <div className="setup-footer">
          <p>Already have an account? <a href="/admin/login">Login here</a></p>
        </div>
      </div>
    </div>
  );
}

export default AdminSetup;
